import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, ButtonToolbar, Form, SelectPicker } from "rsuite";

import "./CommercialFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { CommercialListParams } from "./CommercialDataTypes";
import {
  statusObjArr,
  mediaTypeObjArr,
  mediaSourceObjArr,
  roleTypeArr,
  commercialUseForObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { useNavigate } from "react-router-dom";
import { ContentRoutes } from "../../../navigation/NavConstant";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  showBackToSchedule?: boolean;
}

export default function CommercialFilters({
  onSubmit,
  onClear,
  showBackToSchedule = false,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    media_type: "",
    sourced_from: "",
    media_nature: "",
    commercial_use_for: "",
    company: "",
    brand: "",
    campaign: "",
    status: "",
    smart_search: "",
  };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] =
    useState<CommercialListParams>(initSearchFormData);
  const [brandList, setBrandList] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const company = useSelector((state: any) => state.company);

  function backToCommercialSchedules() {
    navigate(ContentRoutes.COMMERCIAL_SCHEDULES);
  }

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  useEffect(() => {
    if (getCompanyId() !== null) {
      getBrandList("");
      getCampaignList("");
    }
  }, []);

  useEffect(() => {
    if (company.id) {
      getBrandList("");
      getCampaignList("");
    }
  }, [company]);

  async function getBrandList(searchBrand: string) {
    let compId: number = getCompanyId();
    if (!compId) {
      return;
    }
    try {
      setLoading(true);
      const res = await HelperServices.getBrands({
        company: compId,
        name: searchBrand,
      });
      setBrandList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getCampaignList(searchCampaign: string) {
    let compId: number = getCompanyId();
    if (!compId) {
      return;
    }
    try {
      setLoading(true);
      const res = await HelperServices.getCampaigns({
        company: compId,
        name: searchCampaign,
      });
      setCampaignList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function clearSearch() {
    onClear();
    setFormData(initSearchFormData);
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function renderTextField(
    controlId: string,
    label: string,
    name: keyof CommercialListParams
  ) {
    return (
      <div className="commercial-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <Form.Control name={name} />
        </Form.Group>
      </div>
    );
  }

  function renderSelectField({
    controlId,
    label,
    name,
    data,
    valueKey,
    labelKey,
    onSelect,
    onSearch,
  }: {
    controlId: string;
    label: string;
    name: keyof CommercialListParams;
    data: any[];
    valueKey: string;
    labelKey: string;
    onSelect: (value: any) => void;
    onSearch?: (value: string) => void;
  }) {
    return (
      <div className="commercial-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <SelectPicker
            data={data}
            valueKey={valueKey}
            labelKey={labelKey}
            onSearch={onSearch}
            onSelect={(value) => {
              onSelect(value);
            }}
            onClean={() => {
              onSelect("");
            }}
            name={name}
            value={formData?.[name]}
            cleanable
            block
            searchable
            placement="autoVerticalStart"
          />
        </Form.Group>
      </div>
    );
  }

  function renderSection(fields: JSX.Element[], title?: string) {
    if (fields.length === 0) {
      return null;
    }

    return (
      <section className="commercial-filter-section">
        {title && (
          <div className="commercial-filter-section-title">{title}</div>
        )}
        <div className="commercial-filter-grid">{fields}</div>
      </section>
    );
  }

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar className="mb-hide">
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
          {showBackToSchedule && (
            <Button onClick={backToCommercialSchedules}>
              {t("BACK")} {t("TO")} {t("LIST")}
            </Button>
          )}
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content pb-0 mb-3 commercial-filter-panel">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            fluid
            className="commercial-filter-form"
          >
            {renderSection([
              renderTextField(
                "name",
                `${t("COMMERCIAL")} ${t("TITLE")}`,
                "name"
              ),
              renderSelectField({
                controlId: "media_type",
                label: `${t("MEDIA")} ${t("TYPE")}`,
                name: "media_type",
                data: mediaTypeObjArr,
                valueKey: "value",
                labelKey: "label",
                onSelect: (value) =>
                  handleFormChanges({ path: "media_type", value }),
              }),
              renderSelectField({
                controlId: "sourced_from",
                label: `${t("SOURCED")} ${t("FROM")}`,
                name: "sourced_from",
                data: mediaSourceObjArr,
                valueKey: "value",
                labelKey: "label",
                onSelect: (value) =>
                  handleFormChanges({ path: "sourced_from", value }),
              }),
              renderSelectField({
                controlId: "commercial_use_for",
                label: `${t("USE")} ${t("FOR")}`,
                name: "commercial_use_for",
                data: commercialUseForObjArr,
                valueKey: "value",
                labelKey: "label",
                onSelect: (value) =>
                  handleFormChanges({ path: "commercial_use_for", value }),
              }),
              renderSelectField({
                controlId: "status",
                label: t("STATUS"),
                name: "status",
                data: statusObjArr,
                valueKey: "value",
                labelKey: "label",
                onSelect: (value) =>
                  handleFormChanges({ path: "status", value }),
              }),
              renderSelectField({
                controlId: "brand",
                label: t("BRAND"),
                name: "brand",
                data: brandList,
                valueKey: "id",
                labelKey: "brand_name",
                onSearch: (value) => {
                  if (value) {
                    getBrandList(value);
                  }
                },
                onSelect: (value) =>
                  handleFormChanges({ path: "brand", value }),
              }),
              renderSelectField({
                controlId: "campaign",
                label: t("CAMPAIGN"),
                name: "campaign",
                data: campaignList,
                valueKey: "id",
                labelKey: "campaign_name",
                onSearch: (value) => {
                  if (value) {
                    getCampaignList(value);
                  }
                },
                onSelect: (value) =>
                  handleFormChanges({ path: "campaign", value }),
              }),
            ])}

            <div className="filter-btn commercial-filter-actions">
              <Button appearance="subtle" onClick={() => clearSearch()}>
                {t("CLEAR_FILTER")}
              </Button>
              <Button appearance="primary" onClick={() => onSubmit(formData)}>
                {t("SEARCH")}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
